import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DownloadIcon from "@mui/icons-material/Download";
import ClientDashboardLayout from "./ClientDashboardLayout";
import { fetchVaultCaptions, getMvpVaultVideos } from "../../api/lucroVault";
import mvpVaultVideos from "../../data/mvpVaultVideos.json";

const jsonResourceFileById = new Map(
  mvpVaultVideos
    .filter((v) => v.resource_file && String(v.resource_file).trim())
    .map((v) => [String(v.id), String(v.resource_file).trim()])
);

function extractWistiaMediaId(rawUrl) {
  if (!rawUrl) return "";
  try {
    const parsed = new URL(rawUrl);
    const host = parsed.hostname.toLowerCase();
    if (!host.includes("wistia.com")) return "";
    const segments = parsed.pathname.split("/").filter(Boolean);
    const iframeIdx = segments.indexOf("iframe");
    if (iframeIdx >= 0 && segments[iframeIdx + 1]) {
      return segments[iframeIdx + 1];
    }
    const mediasIdx = segments.indexOf("medias");
    if (mediasIdx >= 0 && segments[mediasIdx + 1]) {
      return segments[mediasIdx + 1];
    }
    return "";
  } catch {
    return "";
  }
}

const ALLOWED_EMBED_HOSTS = ["wistia.com", "fast.wistia.com"];

function isAllowedEmbedHost(host) {
  const lower = host.toLowerCase();
  return ALLOWED_EMBED_HOSTS.some(
    (allowed) => lower === allowed || lower.endsWith("." + allowed)
  );
}

/** Returns a safe https embed URL only for Wistia; otherwise "". */
function toPlayableVideoUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return "";
  const trimmed = rawUrl.trim();
  if (!trimmed) return "";
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:") return "";
    const host = url.hostname.toLowerCase();
    if (!isAllowedEmbedHost(host)) return "";
    return url.href;
  } catch {
    return "";
  }
}

function VideoCard({ resource, unlocked, onWatch }) {
  const title = resource?.resource_title || "Video";
  const description = resource?.resource_description || "";
  const tags = resource?.tags || [];
  const thumbnail = resource?.card_image || "/resource-frame.png";
  const videoLink = toPlayableVideoUrl(resource?.external_link);
  const videoDuration = resource?.video_duration;
  // Look up directly from JSON by id, or fall back to what the API provided.
  const directDownloadUrl =
    jsonResourceFileById.get(String(resource?.id)) ||
    (typeof resource?.resource_file === "string" && resource.resource_file.trim()
      ? resource.resource_file.trim()
      : null);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        boxShadow: "0 12px 24px rgba(15,23,42,0.08)",
        border: "1px solid #E5E7EB",
        overflow: "hidden",
      }}
    >
      <CardActionArea
        onClick={unlocked && videoLink ? onWatch : undefined}
        sx={{
          "&.MuiCardActionArea-root": { display: "block" },
          cursor: unlocked && videoLink ? "pointer" : "default",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            pt: "56.25%",
            backgroundImage: `url(${thumbnail})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundColor: "#f0f0f0",
          }}
        >
          {unlocked && videoLink && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0,0,0,0.2)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.35)" },
                transition: "background-color 0.2s",
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 64, color: "#fff" }} />
            </Box>
          )}
        </Box>
      </CardActionArea>
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}>
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, mb: 1 }}>
          {tags.slice(0, 3).map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              sx={{
                bgcolor: tag.label_color || "#E5E7EB",
                fontWeight: 600,
                fontSize: 12,
              }}
            />
          ))}
        </Stack>
        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            flex: 1,
          }}
        >
          {description}
        </Typography>
        {unlocked && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mt: 1.5, flexWrap: "wrap", gap: 0.5 }}
          >
            {videoLink && (
              <CardActionArea
                onClick={onWatch}
                sx={{
                  display: "flex",
                  padding: "6px 10px",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  flex: "1 0 0",
                  borderRadius: "var(--small, 4px)",
                  textTransform: "none",
                  background: "var(--primary-main, #536DEC)",
                   "& .MuiButton-endIcon": { marginLeft: 0.5 },
                  boxShadow:
                    "0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.20)",
                  "&.MuiCardActionArea-root": { display: "inline-flex" },
                  fontFamily: "'Rethink Sans', Helvetica, sans-serif",
                  fontSize: 14,
                  "& .MuiTypography-root": { color: "#fff", fontFamily: "inherit", fontSize: "inherit", textTransform: "none" },
                }}
              >
                <Typography variant="button" fontWeight={600} sx={{ textTransform: "none" }}>
                  Watch now →
                </Typography>
              </CardActionArea>
            )}
            {directDownloadUrl && (
              <Button
                component="a"
                href={directDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                download=""
                endIcon={<DownloadIcon />}
                variant="outlined"
                size="small"
                sx={{
                  padding: "6px 10px",
                  borderRadius: "var(--small, 4px)",
                  borderColor: "var(--primary-main, #536DEC)",
                  color: "var(--primary-main, #536DEC)",
                  fontFamily: "'Rethink Sans', Helvetica, sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  "& .MuiButton-endIcon": { marginLeft: 0.5 },
                  "&:hover": {
                    borderColor: "var(--primary-main, #536DEC)",
                    bgcolor: "rgba(83, 109, 236, 0.08)",
                  },
                }}
              >
                Download
              </Button>
            )}
            {videoDuration ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  component="img"
                  src="/watch.svg"
                  alt=""
                  sx={{ width: 16, height: 16 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {videoDuration} min
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default function VaultPage() {
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const [captionsText, setCaptionsText] = useState("");
  const [captionsLanguage, setCaptionsLanguage] = useState("");
  const selectedVideoLink = toPlayableVideoUrl(selectedVideo?.external_link);
  const selectedVideoDownloadUrl = selectedVideo
    ? jsonResourceFileById.get(String(selectedVideo.id)) ||
      (typeof selectedVideo.resource_file === "string" &&
      selectedVideo.resource_file.trim()
        ? selectedVideo.resource_file.trim()
        : null)
    : null;

  const uniqueTags = React.useMemo(() => {
    const byId = new Map();
    videos.forEach((resource) => {
      (resource?.tags || []).forEach((tag) => {
        if (tag?.id != null && !byId.has(tag.id)) {
          byId.set(tag.id, { id: tag.id, name: tag.name, label_color: tag.label_color });
        }
      });
    });
    return Array.from(byId.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [videos]);

  const filteredVideos =
    tagFilter == null
      ? videos
      : videos.filter((resource) =>
          (resource?.tags || []).some((tag) => Number(tag.id) === Number(tagFilter))
        );

  useEffect(() => {
    let isCancelled = false;

    const loadVideos = async () => {
      setLoadingVideos(true);
      setLoadError("");
      try {
        const data = await getMvpVaultVideos();
        if (!isCancelled) {
          setVideos(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!isCancelled) {
          setVideos([]);
          setLoadError("Could not load vault videos right now.");
        }
      } finally {
        if (!isCancelled) {
          setLoadingVideos(false);
        }
      }
    };

    loadVideos();
    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;
    if (!selectedVideo) {
      setCaptionsText("");
      setCaptionsLanguage("");
      return () => {
        isCancelled = true;
      };
    }
    const wistiaMediaId = extractWistiaMediaId(selectedVideo.external_link);
    if (!wistiaMediaId) {
      setCaptionsText("");
      setCaptionsLanguage("");
      return () => {
        isCancelled = true;
      };
    }
    const loadCaptions = async () => {
      setCaptionsText("");
      setCaptionsLanguage("");
      try {
        const payload = await fetchVaultCaptions(wistiaMediaId);
        const selectedCaption = payload?.selected_caption;
        const nextText =
          typeof selectedCaption?.text === "string"
            ? selectedCaption.text.trim()
            : "";
        if (!isCancelled) {
          if (nextText) {
            setCaptionsText(nextText);
            setCaptionsLanguage(
              selectedCaption?.english_name ||
                selectedCaption?.language ||
                ""
            );
          }
        }
      } catch {
        // Captions panel is hidden when no text; no need to set error state
      }
    };
    loadCaptions();
    return () => {
      isCancelled = true;
    };
  }, [selectedVideo]);

  return (
    <ClientDashboardLayout
      title={selectedVideo ? undefined : null}
      contentMaxWidth="100%"
      contentAlign="left"
    >
      <Box sx={{ width: "100%", minWidth: 0, fontFamily: "'Rethink Sans', Helvetica, sans-serif" }}>
        {loadingVideos ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}
        {!loadingVideos && loadError ? (
          <Typography color="error.main" sx={{ py: 2 }}>
            {loadError}
          </Typography>
        ) : null}
        {!loadingVideos && !loadError && videos.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            No videos available yet.
          </Typography>
        ) : !loadingVideos && !loadError && selectedVideo ? (
          <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 2 }}
            >
              <ButtonBase
                onClick={() => setSelectedVideo(null)}
                sx={{
                  color: "rgba(0,0,0,0.87)",
                  fontFamily: '"Rethink Sans", Helvetica, sans-serif',
                  fontSize: 16,
                  fontWeight: 400,
                }}
              >
                Vault
              </ButtonBase>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", Helvetica, sans-serif',
                  fontSize: "1rem",
                  color: "#9CA3AF",
                  transform: "scale(2)",
                  display: "inline-block",
                }}
              >
                ›
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", Helvetica, sans-serif',
                  fontSize: 16,
                  color: "rgba(0,0,0,0.87)",
                  fontWeight: 600,
                }}
              >
                {selectedVideo.resource_title || "Video"}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ flexWrap: "wrap", gap: 0.75, mb: 1.25 }}
            >
              {(selectedVideo.tags || []).map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  sx={{
                    bgcolor: tag.label_color || "#E5E7EB",
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                />
              ))}
              {selectedVideo.video_duration ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ ml: 0.5 }}
                >
                  <Box
                    component="img"
                    src="/watch.svg"
                    alt=""
                    sx={{ width: 16, height: 16 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {selectedVideo.video_duration} min
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ flexWrap: "wrap", gap: 1.5, mb: 2 }}
            >
              <Typography variant="h5" fontWeight={700} sx={{ flex: "1 1 auto", minWidth: 0 }}>
                {selectedVideo.resource_title}
              </Typography>
              {selectedVideoDownloadUrl && (
                <Button
                  component="a"
                  href={selectedVideoDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download=""
                  endIcon={<DownloadIcon />}
                  variant="contained"
                  size="small"
                  sx={{
                    padding: "6px 10px",
                    borderRadius: "var(--small, 4px)",
                    background: "var(--primary-main, #536DEC)",
                    color: "#fff",
                    fontFamily: "'Rethink Sans', Helvetica, sans-serif",
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    boxShadow:
                      "0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.20)",
                    "& .MuiButton-endIcon": { marginLeft: 0.5 },
                    "&:hover": {
                      background: "var(--primary-main, #536DEC)",
                      filter: "brightness(1.08)",
                    },
                  }}
                >
                  Download Handout
                </Button>
              )}
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: captionsText
                    ? "minmax(0, 2fr) minmax(300px, 1fr)"
                    : "1fr",
                },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {selectedVideoLink ? (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    pt: "56.25%",
                    minHeight: { md: 423 },
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #E5E7EB",
                    bgcolor: "#000",
                  }}
                >
                  <iframe
                    title={selectedVideo.resource_title}
                    src={selectedVideoLink}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #E5E7EB",
                    bgcolor: "rgba(0,0,0,0.03)",
                    p: 3,
                    minHeight: { md: 423 },
                  }}
                >
                  <Typography variant="body1" fontWeight={600}>
                    This video is unavailable.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We could not find a playable video link for this item.
                  </Typography>
                </Box>
              )}
              {captionsText ? (
                <Box
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #E5E7EB",
                    bgcolor: "#fff",
                    p: 2,
                    minHeight: { xs: 260, md: 423 },
                    overflow: "auto",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Captions{captionsLanguage ? ` (${captionsLanguage})` : ""}
                  </Typography>
                  <Typography
                    component="pre"
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      fontFamily: "'Rethink Sans', Helvetica, sans-serif",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    {captionsText}
                  </Typography>
                </Box>
              ) : null}
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              {selectedVideo.resource_description}
            </Typography>
          </Box>
        ) : !loadingVideos && !loadError ? (
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
              sx={{
                pb: 2,
                mb: 2,
                borderBottom: "1px solid rgba(17, 24, 39, 0.08)",
              }}
            >
              <Typography
                sx={{
                  color: "rgba(0, 0, 0, 0.87)",
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  fontFamily: '"Rethink Sans", Helvetica, sans-serif',
                  fontSize: "1.5rem",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: 1.334,
                }}
              >
                Vault
              </Typography>
              {(videos.length > 0 || uniqueTags.length > 0) && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={tagFilter == null ? "" : String(tagFilter)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTagFilter(v === "" ? null : Number(v));
                    }}
                    displayEmpty
                    sx={{
                      fontFamily: '"Rethink Sans", Helvetica, sans-serif',
                      fontSize: 14,
                      borderRadius: "8px",
                      color: "#6B7280",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "8px",
                        borderColor: "#6B7280",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6B7280" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6B7280", borderWidth: "1px" },
                      "& .MuiSelect-icon": { color: "#6B7280" },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: "8px",
                          mt: 1.25,
                          boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                          "& .MuiMenuItem-root": {
                            borderRadius: "8px",
                            mx: 0.75,
                            my: 0.25,
                            "&:hover": {
                              bgcolor: "rgba(83,109,236,0.12)",
                            },
                            "&.Mui-selected": {
                              bgcolor: "rgba(83,109,236,0.08)",
                              "&:hover": {
                                bgcolor: "rgba(83,109,236,0.16)",
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontFamily: '"Rethink Sans", Helvetica, sans-serif' }}>
                      All content
                    </MenuItem>
                    {uniqueTags.map((tag) => (
                      <MenuItem
                        key={tag.id}
                        value={String(tag.id)}
                        sx={{ fontFamily: '"Rethink Sans", Helvetica, sans-serif' }}
                      >
                        {tag.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 2.5,
              }}
            >
            {filteredVideos.map((resource) => (
              <VideoCard
                key={resource.id}
                resource={resource}
                unlocked
                onWatch={() => {
                  setSelectedVideo(resource);
                }}
              />
            ))}
            </Box>
          </Box>
        ) : null}
      </Box>
    </ClientDashboardLayout>
  );
}
